import {
  Button,
  Text,
  VStack,
  Box,
  Tag,
  useDisclosure,
  ButtonGroup,
  IconButton,
  Icon,
  HStack,
  Spacer,
  Spinner,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";

import { CreateOrUpdateCraForecastScenarioModal } from "./CreateOrUpdateCraForecastScenarioModal";
import { CraAttributes, CraAttributeName } from "@keita/lanepatrol-sdk";
import {
  CraForecastChangeType,
  CraForecastAction,
} from "../../../../project/models/CraForecastScenario";
import { parseAttributeInputValue } from "../../../../coding/utils/attribute-value";
import { CraAttributeUtils } from "../../../../coding/utils/attribute-utils";
import { getTreatmentLabel } from "../../../../project/utils/treatment-labels";
import { useStores } from "../../../../shared/stores/RootStore";
import { toast } from "../../../../shared/views/CustomToaster";
import { AddIcon, DeleteIcon, SaveIcon } from "../../../../shared/views/icons";
import { CustomTooltip } from "../../../../shared/views/CustomTooltip";
import { CraForecastActionTooltip } from "./CraForecastActionTooltip";
import { AddCraForecastActionModal } from "./add-forecast-change-modal/AddCraForecastActionModal";

const i18prefix = "map:forecast.display";

const CraForecastControlsBase: React.FC = () => {
  const { craForecastStore, projectStore } = useStores();

  const addForecastChangeModal = useDisclosure();
  const createOrUpdateModal = useDisclosure();
  const selectedProject = projectStore.getSelected();

  const onRemoveChange = (changeIndex: number) => {
    craForecastStore.removeActionByIndex(changeIndex);
  };

  const onDelete = () => {
    if (
      window.confirm(
        t(`${i18prefix}.delete.confirm`, {
          name: craForecastStore.selectedScenario!.name,
        }),
      )
    ) {
      craForecastStore
        .deleteForecastScenario(craForecastStore.selectedScenario!.id)
        .then(() => toast("success", t(`${i18prefix}.delete.success`)))
        .catch(() => toast("error", t(`${i18prefix}.delete.error`)));
    }
  };

  return (
    <>
      <VStack align="stretch">
        <HStack>
          <Text fontSize="sm">{t(`${i18prefix}.title`)}</Text>
          <Spacer />
          {craForecastStore.computingValues && (
            <HStack>
              <Spinner color="primary.500" size="xs" />
              <Text fontSize="xs">{t(`${i18prefix}.loading`)}</Text>
            </HStack>
          )}
        </HStack>

        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            fontSize={"sm"}
            value={craForecastStore.selectedScenario?.id ?? ""}
            onChange={(e) =>
              craForecastStore.setSelectedScenario(e.target.value)
            }
            placeholder={
              craForecastStore.scenarios.length
                ? t(`${i18prefix}.select`)
                : t(`${i18prefix}.selectEmpty`)
            }
          >
            {craForecastStore.scenarios.map((scenario) => (
              <option value={scenario.id} key={scenario.id}>
                {scenario.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        {selectedProject?.permissions.canManageResults && (
          <>
            <ButtonGroup
              size={"sm"}
              colorPalette="primary"
              variant="outline"
              fontSize="sm"
            >
              <Button
                flexGrow={1}
                variant="outline"
                fontSize={"sm"}
                size="sm"
                colorPalette="primary"
                onClick={addForecastChangeModal.onOpen}
              >
                <AddIcon /> {t(`${i18prefix}.addAction`)}
              </Button>
              <IconButton
                onClick={createOrUpdateModal.onOpen}
                disabled={!craForecastStore.actions.length}
              >
                <Icon as={SaveIcon} />
              </IconButton>
              <IconButton
                disabled={!craForecastStore.selectedScenario}
                onClick={onDelete}
              >
                <DeleteIcon />
              </IconButton>
            </ButtonGroup>
          </>
        )}

        <Box m={-1}>
          {craForecastStore.actions.map((change, i) => (
            <CustomTooltip
              key={i}
              content={<CraForecastActionTooltip change={change} />}
            >
              <Tag.Root m={1} py={1} fontSize="xs">
                <Tag.Label>{getChangeLabel(change)}</Tag.Label>

                {selectedProject?.permissions.canManageResults && (
                  <Tag.EndElement>
                    <Tag.CloseTrigger onClick={() => onRemoveChange(i)} />
                  </Tag.EndElement>
                )}
              </Tag.Root>
            </CustomTooltip>
          ))}
        </Box>
      </VStack>

      {createOrUpdateModal.open && (
        <CreateOrUpdateCraForecastScenarioModal
          onClose={createOrUpdateModal.onClose}
        />
      )}

      {addForecastChangeModal.open && (
        <AddCraForecastActionModal onClose={addForecastChangeModal.onClose} />
      )}
    </>
  );
};

export const CraForecastControls = observer(CraForecastControlsBase);

const getChangeLabel = (action: CraForecastAction): string => {
  switch (action.change.type) {
    case CraForecastChangeType.AttributeValues: {
      const attributeLabels = (
        Object.entries(action.change.attributeValues) as [
          CraAttributeName,
          string,
        ][]
      )
        .map(([name, stringValue]) => {
          const attribute = CraAttributes.getByName(name)!;
          const value = parseAttributeInputValue(attribute, stringValue);
          return `${CraAttributeUtils.getLabel(
            attribute.name,
          )} = ${CraAttributeUtils.getValueLabel(attribute, value)}`;
        })
        .join(", ");
      return t(`map:forecast.${action.change.type}.tag`, {
        attributes: attributeLabels,
        count: action.results?.size ?? 0,
      });
    }
    case CraForecastChangeType.TreatmentWhenSuggested: {
      return t(`map:forecast.${action.change.type}.tag`, {
        treatment: getTreatmentLabel(action.change.treatmentType),
        count: action.results?.size ?? 0,
      });
    }
    case CraForecastChangeType.HighestPriorityTreatment: {
      return t(`map:forecast.${action.change.type}.tag`, {
        count: action.results?.size ?? 0,
      });
    }
    default: {
      return "Not implemented yet";
    }
  }
};
